import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { QrCode, Instagram } from "lucide-react";
import LanguageSwitcher from "./LanguageSwitcher";
import ThemeSwitcher from "./ThemeSwitcher";
import { useLang } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { COMPANY } from "../config/company";
import { useLocalizedHref } from "../hooks/useLocalizedSEO";

export default function Navbar() {
  const { t } = useLang();
  const { user } = useAuth();
  const loc = useLocation();
  const lh = useLocalizedHref();
  const isPublic = loc.pathname.startsWith("/m/");
  if (isPublic) return null;

  return (
    <header className="sticky top-0 z-40 glass" data-testid="navbar">
      <div className="container mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
        <Link to={lh("/")} className="flex items-center gap-2 group" data-testid="navbar-logo">
          <div className="w-9 h-9 rounded-xl gradient-bg grid place-items-center text-white shadow-lg shadow-purple-500/30 group-hover:scale-105 transition-transform">
            <QrCode className="h-5 w-5" />
          </div>
          <span className="font-display font-bold text-lg tracking-tight">qrmenu-pro.com</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          <Link to={lh("/")} data-testid="nav-home">
            <Button variant="ghost" size="sm" className="rounded-full">{t("common.home")}</Button>
          </Link>
          <Link to={lh("/pricing")} data-testid="nav-pricing">
            <Button variant="ghost" size="sm" className="rounded-full">{t("common.pricing")}</Button>
          </Link>
          {user && user !== false && (
            <Link to="/dashboard" data-testid="nav-dashboard">
              <Button variant="ghost" size="sm" className="rounded-full">{t("common.dashboard")}</Button>
            </Link>
          )}
        </nav>

        <div className="flex items-center gap-1">
          <a
            href={COMPANY.instagramUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Instagram @${COMPANY.instagramHandle}`}
            title="Instagram"
            className="hidden sm:inline-flex items-center justify-center w-9 h-9 rounded-full text-zinc-500 hover:text-white hover:bg-gradient-to-tr hover:from-[#f09433] hover:via-[#dc2743] hover:to-[#bc1888] transition-all duration-300 hover:scale-110"
            data-testid="nav-instagram"
          >
            <Instagram className="h-4 w-4" />
          </a>
          <LanguageSwitcher />
          <ThemeSwitcher />
          {user && user !== false ? (
            <Link to="/dashboard">
              <Button size="sm" className="rounded-full bg-purple-600 hover:bg-purple-700 text-white shadow-lg shadow-purple-500/25 ml-1" data-testid="nav-cta-dashboard">
                {t("common.dashboard")}
              </Button>
            </Link>
          ) : (
            <>
              <Link to="/login" className="hidden sm:block">
                <Button variant="ghost" size="sm" className="rounded-full" data-testid="nav-login">{t("common.login")}</Button>
              </Link>
              <Link to="/register">
                <Button size="sm" className="rounded-full bg-purple-600 hover:bg-purple-700 text-white shadow-lg shadow-purple-500/25 ml-1" data-testid="nav-register">
                  {t("common.register")}
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
